import { useTranslator } from "@eo-locale/react";

import find from "lodash/find";

import { useRouter } from "next/router";

import { useEffect } from "react";

import {
  Text,
  Heading,
  Box,
  HTMLWrapper,
  Button
} from "@/storybook-components";
import { useAppSelector, useAppDispatch } from "@/redux/store/hooks";
import ResendVerificationCode from "./resend-verification-code";
import VerificationForm from "./verification-form";
import { signinUser } from "@/helpers/signin-user";

import { resetAnswers } from "@/redux/features/questions/slice";
import { resetRegistrations } from "@/redux/features/registrations/slice";
import { stripAllHtml } from "@/lib";
import Alerts from "@/components/alerts";
import isRelativePath from "@/helpers/is-relative-path";

const VerifyAccount = (): JSX.Element => {
  const router = useRouter();
  const { translate } = useTranslator();
  const dispatch = useAppDispatch();
  const { isOtpVerifiedSuccessfully } = useAppSelector(
    (state) => state.userConfirmation
  );
  const { registrations } = useAppSelector((state) => state.registrations);
  const { signupFormQuestions } = useAppSelector(
    (state) => state.signupFormQuestions
  );
  const { answers } = useAppSelector((state) => state.questions);
  const { isLoggedIn } = useAppSelector((state) => state.user.registeredUser);
  const { welcomePage } = useAppSelector(
    (state) => state.messageText.messageText
  );
  const nextPageUrl = router.asPath.split("?next=")[1] || null;

  // Redirect user after login
  useEffect(() => {
    if (isLoggedIn) {
      if (nextPageUrl && isRelativePath(nextPageUrl)) router.push(nextPageUrl);
      else router.push("/");
    }
  }, [isLoggedIn]);

  const getPassword = () => {
    const question: {
      id: string;
    } = find(signupFormQuestions, ["type", "SignupForm::PasswordQuestion"]);

    if (question) return answers[question.id];
    return "";
  };

  const handleLoginAndRedirect = async (): Promise<void> => {
    signinUser(registrations.email, getPassword(), "1");
  };

  const handleStartAgain = () => {
    dispatch(resetRegistrations());
    dispatch(resetAnswers());
  };

  const isWelcomeMessagePresent = (txt) => {
    return stripAllHtml(txt).length > 0;
  };

  if (isOtpVerifiedSuccessfully) {
    const isWelcomeMsg = isWelcomeMessagePresent(welcomePage);
    return (
      <Box textAlign="center">
        {isWelcomeMsg ? (
          <HTMLWrapper my={4} html={welcomePage as string} />
        ) : (
          <Alerts
            flexDir={{ base: "column" }}
            status="success"
            title={translate("register.congratulations", {
              login: registrations.login
            })}
            description={translate("register.accountCreated")}
          />
        )}
        <Button
          variant="primary"
          width={{ md: "auto", sm: "100%" }}
          mt={{ md: 12, sm: 6 }}
          onClick={handleLoginAndRedirect}
          alignSelf={{ base: "center", sm: "flex-start" }}
          data-testid="accountVerification-continueButton"
        >
          {translate("common.continue")}
        </Button>
      </Box>
    );
  }

  return (
    <>
      <Heading as="h1" my={3} data-testid="accountVerification-heading">
        {translate("register.verifyAccount")}
      </Heading>
      <VerificationForm />
      <ResendVerificationCode />
      <Text my="2">
        {translate("register.wrongEmail")} &nbsp;
        <Button
          variant="link"
          onClick={handleStartAgain}
          data-testid="accountVerification-startOverButton"
        >
          {translate("register.startOver")}
        </Button>
      </Text>
    </>
  );
};

export default VerifyAccount;
